import { chunkUint8Array } from '../utils';
import {
    requireInitMessageUpdate,
    requirePromptInitMessageStateVector,
    requireUpdateMessageUpdate,
} from './messages';
import * as protocol from './protocol';

/**
 * Cloudflare has a maximum size of 1MB for the message sent to the client.
 */
const MAX_MESSAGE_SIZE = 0.9 * 1024 * 1024;

export type DataSyncEncoderWriteFn = (input: {
    chunk: Uint8Array;
    message: protocol.IMessage;
    chunkIndex: number;
    chunkCount: number;
}) => void;

export type DataSyncEncoder = {
    newMessageID: () => number;
    write: (message: protocol.IMessage) => void;
};

/**
 * Encode messages to be sent over the network.
 * With support for chunking messages to fit within the maximum message size.
 */
export function createDataSyncEncoder(
    write: DataSyncEncoderWriteFn,
    initialCurrentMessage = 0
): DataSyncEncoder {
    let currentMessage = initialCurrentMessage;

    return {
        newMessageID: () => {
            currentMessage += 1;
            return currentMessage;
        },
        write: (message) => {
            if (message.init) {
                const init = message.init;
                chunkBinaryMessage(requireInitMessageUpdate(init), (chunk, index, count) => {
                    write({
                        chunk: encodeDataSyncMessage({
                            init: {
                                ...init,
                                index,
                                count,
                                update: chunk,
                            },
                        }),
                        message,
                        chunkIndex: index,
                        chunkCount: count,
                    });
                });
            } else if (message.update) {
                const update = message.update;
                chunkBinaryMessage(requireUpdateMessageUpdate(update), (chunk, index, count) => {
                    write({
                        chunk: encodeDataSyncMessage({
                            update: {
                                ...update,
                                index,
                                count,
                                update: chunk,
                            },
                        }),
                        message,
                        chunkIndex: index,
                        chunkCount: count,
                    });
                });
            } else if (message.promptInit) {
                const promptInit = message.promptInit;
                chunkBinaryMessage(
                    requirePromptInitMessageStateVector(promptInit),
                    (chunk, index, count) => {
                        write({
                            chunk: encodeDataSyncMessage({
                                promptInit: {
                                    ...promptInit,
                                    index,
                                    count,
                                    stateVector: chunk,
                                },
                            }),
                            message,
                            chunkIndex: index,
                            chunkCount: count,
                        });
                    }
                );
            } else {
                write({
                    chunk: encodeDataSyncMessage(message),
                    chunkCount: 1,
                    chunkIndex: 0,
                    message,
                });
            }
        },
    };
}

/**
 * Encode a DataSync message.
 */
function encodeDataSyncMessage(input: protocol.IMessage): Uint8Array {
    const verification = protocol.Message.verify(input);
    if (verification) {
        throw new Error(`ProtoBufObject message is not valid: ${verification}`);
    }

    const message = protocol.Message.create(input);
    return protocol.Message.encode(message).finish();
}

/**
 * Chunk an update or init message.
 */
function chunkBinaryMessage(
    update: Uint8Array,
    callback: (chunk: Uint8Array, index: number, count: number) => void
) {
    const chunks = chunkUint8Array(update, MAX_MESSAGE_SIZE);

    chunks.forEach((chunk, index, arr) => {
        callback(chunk, index, arr.length);
    });
}
